<script setup>
import { ref, computed, watch } from 'vue';
import { useActions, useGetters } from '@/utils/mapStore';
import NewCusDialog from '@/library/NewCusDialog.vue';
const doPushInfoForGuest = useActions('order', ['doPushInfoForGuest']);
const doSetMemberPhone = useActions('member', ['doSetMemberPhone']);

const getOrderId = useGetters('order', ['getOrderId']);

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  caller: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'checked']);

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

if (props.caller) {
  console.log(`MemberDialog called from: ${props.caller}`);
}

const passButtonText = computed(() => {
  return getOrderId.value ? '取消' : '直接進入點餐';
});

const phoneNumber = ref('09');
// 新增 errorMsg 變數儲存驗證錯誤資訊
const errorMsg = ref('');
const showConfirm = ref(false);
// 修改 isValidPhone 條件，必須是10碼且以"09"開始
const isValidPhone = computed(() => phoneNumber.value.length === 10 && phoneNumber.value.startsWith('09'));

// 修改 formattedPhoneNumber computed property
const formattedPhoneNumber = computed(() => {
  if (phoneNumber.value === '') return ''; // 顯示 placeholder: 請輸入會員手機號碼
  const digits = phoneNumber.value.padEnd(10, '_').split('');
  return `${digits.slice(0, 4).join(' ')}-${digits.slice(4, 7).join(' ')}-${digits.slice(7, 10).join(' ')}`;
});

// 新增 computed property displayPhoneNumber，用來產生逐字顯示的陣列
const displayPhoneNumber = computed(() => {
  if (phoneNumber.value === '') return [];
  const padded = phoneNumber.value.padEnd(10, '_');
  const result = [];
  for (let i = 0; i < 10; i++) {
    result.push({ char: padded[i], isDigit: /\d/.test(padded[i]) });
    if (i === 3 || i === 6) {
      result.push({ char: '-', isDigit: false });
    }
  }
  return result;
});

// 新增 watch 來即時更新錯誤提示
watch(phoneNumber, (newVal) => {
  if (newVal === '') {
    errorMsg.value = '';
  } else if (newVal.length === 1) {
    errorMsg.value = newVal !== '0' ? '手機號碼必須以09開頭' : '';
  } else if (newVal.length >= 2) {
    errorMsg.value = !newVal.startsWith('09') ? '手機號碼必須以09開頭' : '';
  }
});

watch(() => props.modelValue, (newValue) => {
  if (newValue === true) {
    phoneNumber.value = '09';
    errorMsg.value = '';
  }
});

let messageResolve = null;
let messageReject = null;

// 修改 handleNumber 函式以阻止非 09 開頭輸入
const handleNumber = (num) => {
  if (phoneNumber.value.length === 0 && num !== 0) {
    errorMsg.value = '手機號碼必須以09開頭';
    return;
  }
  if (phoneNumber.value.length === 1 && phoneNumber.value === '0' && num !== 9) {
    errorMsg.value = '手機號碼必須以09開頭';
    return;
  }
  if (phoneNumber.value.length >= 10) return;
  phoneNumber.value += num.toString();
  errorMsg.value = '';
};

const handleBack = () => {
  phoneNumber.value = phoneNumber.value.slice(0, -1);
};

const handleCancel = () => {
  visible.value = false;
  phoneNumber.value = '09';
  errorMsg.value = '';
};
const handleMessageCancel = () => {
  showConfirm.value = false; // 只關閉確認視窗,不關閉整個 dialog
  doPushInfoForGuest({
    type: 'check_member_phone',
    data: { dialogState: false, memberPhone: displayPhoneNumber.value },
  });
};

const handleMessageConfirm = async () => {
  doPushInfoForGuest({
    type: 'check_member_phone',
    data: { dialogState: false, memberPhone: displayPhoneNumber.value },
  });
  emit('checked', phoneNumber.value); // 發送 checked 事件並傳遞電話號碼
  visible.value = false;
  showConfirm.value = false;
  doSetMemberPhone(phoneNumber.value); //設定輸入的門號
};

const handleConfirm = () => {
  console.log('handleConfirm');
  doPushInfoForGuest({
    type: 'check_member_phone',
    data: { dialogState: true, memberPhone: displayPhoneNumber.value },
  });
  showConfirm.value = true;
};

const handlePassButtonClick = () => {
  if (getOrderId.value) {
    handleCancel();
  } else {
    handlePassMemberInput();
  }
};

//放棄輸入會員，直接點餐
const handlePassMemberInput = () => {
  emit('checked', ''); // 發送 checked 事件並傳遞電話號碼
  visible.value = false;
  showConfirm.value = false;
  doSetMemberPhone(''); //門號設定為空值，代表不輸入
};

function handleConfirmDialogClose() {
  // alert("xxx");
  // 在這裡處理對話框關閉事件
  console.log('對話框已關閉');
  // 做任何您需要的處理
  handleMessageCancel();
}
//處理pos被關起來
function handleVisibilityChange(newValue) {
  if (newValue === false) {
    console.log('對話框已關閉');
    // 在這裡執行對話框關閉後的操作
    doPushInfoForGuest({
      type: 'check_member_phone',
      data: { dialogState: false, memberPhone: displayPhoneNumber.value },
    });
  }
}

// 新增清除電話輸入的函式
const handleClear = () => {
  phoneNumber.value = '';
  errorMsg.value = '';
};
</script>

<template>
  <new-cus-dialog
    v-model:visible="visible"
    @update:visible="handleVisibilityChange"
    width="500"
    height="580"
    :hideClose="true"
  >
    <template #header>
      <div class="dialog-header">
        <cus-button
          :style="{ visibility: getOrderId ? 'hidden' : 'visible' }"
          color="white"
          :width="25"
          @click="handleCancel"
        >
          <i class="bi bi-arrow-left" style="margin-right: 6px"></i>
          <span style="font-size: 16px">返回</span>
        </cus-button>
        <p class="">請輸入手機會員</p>
        <div style="width: 100px"></div>
      </div>
    </template>
    <template #body>
      <div class="body-content">
        <div class="result-box">
          <!-- 修改：根據 phoneNumber 是否為空決定呈現 placeholder 或依序顯示各字元 -->
          <p class="result">
            <template v-if="phoneNumber === ''">
              <span style="color: gray; font-size: 16px">請輸入會員手機號碼</span>
            </template>
            <template v-else>
              <span
                v-for="(item, index) in displayPhoneNumber"
                :key="index"
                :style="{
                  color: item.isDigit ? 'green' : 'gray',
                  fontWeight: item.isDigit ? 'bolder' : 'normal',
                  'letter-spacing': '6px',
                }"
              >
                {{ item.char }}
              </span>
            </template>
          </p>
          <!-- 將 m-error-msg 移入 m-result-box 內 -->
          <div class="error-msg">
            <p>{{ errorMsg }}</p>
          </div>
        </div>
        <div class="number-button">
          <cus-button v-for="num in 9" :key="num" :text="num.toString()" @click="handleNumber(num)"></cus-button>
          <!-- 修改後：新增「清除」按鈕，接著「0」與「<-" 按鈕 -->
          <cus-button @click="handleClear">
            <template #default>
              <span style="color: red">清除</span>
            </template>
          </cus-button>
          <cus-button text="0" @click="handleNumber(0)"></cus-button>
          <cus-button
            topIcon="arrow-left"
            topIconClass="back-arrow"
            :disabled="!phoneNumber"
            @click="handleBack"
          ></cus-button>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="dialog-footer">
        <cus-button :text="passButtonText" color="white" :width="50" @click="handlePassButtonClick"></cus-button>
        <cus-button text="確定" color="blue" :width="50" @click="handleConfirm" :disabled="!isValidPhone"></cus-button>
      </div>
    </template>
  </new-cus-dialog>

  <new-cus-dialog v-model:visible="showConfirm" width="460" title="會員電話確認" @close="handleConfirmDialogClose">
    <template #body>
      <div class="message-content-body">
        <h1>
          <span
            v-for="(item, index) in displayPhoneNumber"
            :key="index"
            :style="{ color: item.isDigit ? 'green' : 'gray', 'letter-spacing': '6px' }"
          >
            {{ item.char }}
          </span>
        </h1>
        <p>請顧客確認客顯畫面，會員電話是否輸入正確</p>
      </div>
    </template>
    <template #footer>
      <div class="d-flex">
        <cus-button text="重新輸入" color="white" :width="50" @click="handleMessageCancel"></cus-button>
        <cus-button text="確定" color="blue" :width="50" @click="handleMessageConfirm"></cus-button>
      </div>
    </template>
  </new-cus-dialog>
</template>

<style lang="scss" scoped>
* {
  // outline: 1px solid #000;
}

.dialog-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  p {
    text-align: center;
    font-size: 2.4rem;
    line-height: 50px;
    color: $color-text;
  }
}
.body-content {
  display: flex;
  flex-direction: column;
  height: 100%; // 修改：確保父容器有明確高度
}

.result-box {
  margin-bottom: 6px;
  .result {
    width: 100%;
    text-align: center;
    font-size: 4.8rem;
    min-height: 60px; // 新增：固定輸入數字顯示區的最小高度
    line-height: 60px; // 可讓內容垂直置中
    // line-height: 1; // 調整行高
    height: 60px; // 固定高度
    display: flex; // 使用 Flex 置中
    align-items: center;
    justify-content: center;
    &.placeholder {
      font-size: 20px;
      color: #ccc; // 使用較淡的文字顏色
    }
  }
  // 將 m-error-msg 內嵌在 m-result-box 並給定固定高度
  .error-msg {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    width: 100%;
    line-height: 1.5rem;
    font-size: 12px;
    text-align: center;
    color: red;
  }
}

.number-button {
  flex-grow: 1; // 修改：撐滿剩餘空間
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  // margin-bottom: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 4px;
  > :deep(*) {
    // 新增這個選擇器
    min-height: 100%;
  }
  // padding: 4px;
}

.message-content-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  h1 {
    margin: 0 auto 12px;
    span {
      font-size: 4rem;
      color: $color-green;
      letter-spacing: 6px;
    }
  }
  p {
    font-size: 1.8rem;
    color: $color-text;
  }
}
</style>
